import { useMemo, useState } from 'react'
import { AlertTriangle, Clock, Copy, KeyRound } from 'lucide-react'
import { apiPostJson } from '../../lib/apiClient'

type CursorKeyRow = {
  order_code: string
  key: string
  expires_at: string
  duration_days?: number | null
}

type DigigoTokenResolveResponse =
  | { success: true; code: 'OK'; key: string; expires_at: string; purchase_url?: string }
  | { success: false; code: string; message: string; purchase_url?: string; duration_days?: number }

function formatViDateTime(iso?: string | null) {
  if (!iso) return '—'
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return '—'
  return new Intl.DateTimeFormat('vi-VN', {
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  }).format(d)
}

function getErrorMessage(err: unknown) {
  if (!err) return 'Có lỗi xảy ra. Vui lòng thử lại.'
  if (typeof err === 'string') return err
  if (typeof err === 'object' && 'message' in err && typeof (err as any).message === 'string') {
    const message = (err as any).message as string
    if (message.toLowerCase().includes('failed to fetch')) {
      return 'TypeError: Failed to fetch'
    }
    return message
  }
  return 'Có lỗi xảy ra. Vui lòng thử lại.'
}

export default function CursorTab() {
  const [orderCode, setOrderCode] = useState('')
  const normalizedOrderCode = useMemo(() => orderCode.trim(), [orderCode])

  const [isSubmitting, setIsSubmitting] = useState(false)
  const [error, setError] = useState('')
  const [success, setSuccess] = useState('')
  const [result, setResult] = useState<CursorKeyRow | null>(null)

  const handleCopy = async (text: string) => {
    if (!text) return
    setError('')
    setSuccess('')
    try {
      await navigator.clipboard.writeText(text)
      setSuccess('Đã copy')
    } catch {
      setError('Không thể copy')
    }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')
    setSuccess('')
    setResult(null)

    if (!normalizedOrderCode) {
      setError('Vui lòng nhập mã đơn')
      return
    }

    setIsSubmitting(true)
    try {
      const resp = await apiPostJson<{ success: boolean; data?: DigigoTokenResolveResponse }>('/api/utilities/cursor/resolve-by-order', { order_id: normalizedOrderCode })
      const json = resp?.data as DigigoTokenResolveResponse
      if (!resp?.success || !json) throw new Error('Lỗi server')

      if (json.success) {
        setResult({ order_code: normalizedOrderCode, key: json.key, expires_at: json.expires_at })
        setSuccess('Lấy key thành công')
        return
      }

      if (json.code === 'ORDER_NOT_ACTIVATED') {
        const resp2 = await apiPostJson<{ success: boolean; data?: DigigoTokenResolveResponse }>('/api/utilities/cursor/activate-by-order', { order_id: normalizedOrderCode })
        const activated = resp2?.data as DigigoTokenResolveResponse
        if (!resp2?.success || !activated) throw new Error('Lỗi server')
        if (activated.success) {
          setResult({ order_code: normalizedOrderCode, key: activated.key, expires_at: activated.expires_at })
          setSuccess('Tạo key thành công')
          return
        }
        throw new Error(activated.message || 'Không thể kích hoạt')
      }

      throw new Error(json.message || 'Có lỗi xảy ra. Vui lòng thử lại.')
    } catch (err) {
      console.error('CursorTab submit error', { err })
      setError(getErrorMessage(err))
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <div className="animate-fade-in">
      <div className="mx-auto max-w-3xl">
        <div className="bg-white rounded-2xl shadow-sm border border-gray-200 overflow-hidden">
          <div className="px-6 py-5 border-b border-gray-200">
            <h2 className="text-xl font-extrabold text-gray-900">Cursor</h2>
            <p className="text-sm text-gray-600">Nhập mã đơn để lấy key. Nếu chưa từng nhập sẽ tự tạo key UUID v7.</p>
          </div>

          <div className="px-6 py-6 space-y-6">
            {(error || success) && (
              <div className="space-y-2">
                {error && (
                  <div className="p-4 rounded-lg bg-red-50 border border-red-200 text-red-700 text-sm flex gap-2">
                    <AlertTriangle className="w-5 h-5 mt-0.5" />
                    <div>{error}</div>
                  </div>
                )}
                {success && (
                  <div className="p-4 rounded-lg bg-green-50 border border-green-200 text-green-800 text-sm flex gap-2">
                    <Clock className="w-5 h-5 mt-0.5" />
                    <div>{success}</div>
                  </div>
                )}
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Mã đơn</label>
                <input
                  value={orderCode}
                  onChange={(e) => setOrderCode(e.target.value)}
                  placeholder="Nhập mã đơn"
                  className="w-full px-4 py-3 rounded-xl border border-gray-300 focus:ring-2 focus:ring-amber-500 focus:border-amber-500 outline-none transition-all"
                  disabled={isSubmitting}
                />
              </div>

              <button
                type="submit"
                disabled={isSubmitting || !normalizedOrderCode}
                className="w-full bg-amber-600 text-white px-6 py-3 rounded-xl font-semibold hover:bg-amber-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                <KeyRound className="w-5 h-5" />
                {isSubmitting ? 'Đang xử lý...' : 'Tạo / Lấy key'}
              </button>
            </form>

            {result && (
              <div className="space-y-4">
                <div className="grid gap-3">
                  <div className="p-4 rounded-xl border border-gray-200 bg-gray-50">
                    <div className="text-sm text-gray-500">Key</div>
                    <div className="mt-1 flex items-center justify-between gap-2">
                      <div className="font-mono text-sm text-gray-900 break-all">{result.key}</div>
                      <button
                        onClick={() => void handleCopy(result.key)}
                        className="p-2 rounded-lg hover:bg-gray-200 transition-colors"
                        title="Sao chép"
                        type="button"
                      >
                        <Copy className="w-4 h-4" />
                      </button>
                    </div>
                  </div>

                  <div className="p-4 rounded-xl border border-gray-200 bg-gray-50">
                    <div className="text-sm text-gray-500">Hết hạn</div>
                    <div className="mt-1 font-semibold text-gray-900">{formatViDateTime(result.expires_at)}</div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

